<template>
	<v-card class="time-slots-editor elevation-0 mb-4" outlined>
		<v-card-title class="pa-3 background-1">
			<v-icon color="primary" class="mr-2">mdi-clock-outline</v-icon>
			<span class="subtitle-1 font-weight-medium">{{ field.label || 'Fasce Orarie' }}</span>
			<v-spacer></v-spacer>
		</v-card-title>

		<v-divider></v-divider>

		<v-card-text class="pa-4 background-2">

			<div class="d-flex align-center mb-4">
				<div>
					<p class="caption grey--text mb-0">
						{{ field.hint || 'Configura le fasce orarie predefinite (es. Mattina, Pomeriggio, All-day).' }}
					</p>
				</div>
				<v-spacer></v-spacer>
				<v-btn color="primary"
					small
					@click="createStagingSlot"
					:disabled="hasStagingSlot">
					<v-icon left small>mdi-plus</v-icon>
					Aggiungi Fascia
				</v-btn>
			</div>

			<!-- Staging slot (create/edit) -->
			<v-card v-if="hasStagingSlot"
				class="mb-3 background-1"
				outlined>
				<v-card-text class="pa-3">
					<div class="d-flex align-center mb-2">
						<v-icon color="warning" class="mr-2">mdi-clock-plus-outline</v-icon>
						<span class="font-weight-medium">
							{{ isEditingStaging ? 'Modifica Fascia Oraria' : 'Nuova Fascia Oraria' }}
						</span>
						<v-spacer></v-spacer>
						<v-btn icon x-small @click="cancelStagingSlot">
							<v-icon small>mdi-close</v-icon>
						</v-btn>
					</div>

					<v-alert dense text color="info" icon="mdi-information-outline" class="ma-2 mb-3 caption">
						<strong>Suggerimento:</strong> per una fascia che copre l'intera giornata usa <strong>00:00 - 24:00</strong> oppure <strong>08:00 - 18:00</strong>.
					</v-alert>

					<v-row class="ma-0">
						<v-col cols="12" md="5" class="px-2 py-1">
							<v-text-field
								v-model="stagingSlot.name"
								label="Descrizione"
								outlined
								dense
								hide-details="auto"
								placeholder="es: Mattina, Pomeriggio, All-day..."
								:error-messages="getStagingValidationError('name')"
								@input="validateStagingSlot"
							></v-text-field>
						</v-col>

						<v-col cols="6" md="3" class="px-2 py-1">
							<v-text-field
								v-model="stagingSlot.time_from"
								label="Dalle"
								outlined
								dense
								hide-details="auto"
								v-mask="'##:##'"
								placeholder="HH:MM"
								:error-messages="getStagingValidationError('time_from')"
								@input="validateStagingSlot"
							></v-text-field>
						</v-col>

						<v-col cols="6" md="3" class="px-2 py-1">
							<v-text-field
								v-model="stagingSlot.time_to"
								label="Alle"
								outlined
								dense
								hide-details="auto"
								v-mask="'##:##'"
								placeholder="HH:MM"
								:error-messages="getStagingValidationError('time_to')"
								@input="validateStagingSlot"
							></v-text-field>
						</v-col>
					</v-row>

					<div class="d-flex justify-end mt-2">
						<v-btn color="success" small @click="saveStagingSlot" :disabled="!canSaveStaging">
							<v-icon left small>mdi-check</v-icon>
							{{ isEditingStaging ? 'Salva' : 'Aggiungi' }}
						</v-btn>
					</div>
				</v-card-text>
			</v-card>

			<!-- Lista fasce configurate -->
			<div v-if="slots.length > 0">
				<v-card
					v-for="(slot, index) in slots"
					:key="`slot_${index}`"
					class="mb-2 background-1"
					:class="{ 'editing-slot': isEditingStaging && stagingEditIndex === index }"
					outlined>
					<v-card-text class="pa-3">
						<div class="d-flex align-center">
							<v-icon color="primary" class="mr-3">mdi-clock-time-four-outline</v-icon>
							<div class="flex-grow-1">
								<div class="font-weight-medium">
									{{ slot.name || 'Fascia senza nome' }}
								</div>
								<div class="caption grey--text">
									{{ slot.time_from }} - {{ slot.time_to }}
								</div>
							</div>
							<v-btn icon small color="primary"
								@click="editSlot(index)"
								:disabled="hasStagingSlot"
								class="mr-2">
								<v-icon small>mdi-pencil</v-icon>
							</v-btn>
							<v-btn icon small color="error"
								@click="deleteSlot(index)"
								:disabled="hasStagingSlot">
								<v-icon small>mdi-delete</v-icon>
							</v-btn>
						</div>
					</v-card-text>
				</v-card>
			</div>

			<!-- Empty state -->
			<v-card v-else flat class="mb-2 background-1 text-center pa-6">
				<v-icon size="64" color="grey lighten-1">mdi-clock-outline</v-icon>
				<p class="grey--text mt-3 mb-0">
					Nessuna fascia oraria configurata
				</p>
				<v-btn color="primary" class="mt-3" small outlined @click="createStagingSlot" :disabled="hasStagingSlot">
					<v-icon left small>mdi-plus</v-icon>
					Aggiungi Prima Fascia
				</v-btn>
			</v-card>

		</v-card-text>
	</v-card>
</template>

<script>
import Vue from 'vue'

export default {
	name: 'time-slots-editor',

	props: {
		field: {
			type: Object,
			required: true,
		},
		model: {
			type: Object,
			required: true,
		},
	},

	data() {
		return {
			stagingSlot: null,
			stagingEditIndex: null,
			stagingValidationErrors: {},
		}
	},

	computed: {
		slots() {
			const value = this.model[this.field.key]
			return Array.isArray(value) ? value : []
		},
		hasStagingSlot() {
			return this.stagingSlot !== null
		},
		isEditingStaging() {
			return this.hasStagingSlot && this.stagingEditIndex !== null
		},
		canSaveStaging() {
			if (!this.hasStagingSlot) return false
			return this.stagingSlot.name
				&& this.isValidTime(this.stagingSlot.time_from)
				&& this.isValidTime(this.stagingSlot.time_to)
				&& this.timeToMinutes(this.stagingSlot.time_from) < this.timeToMinutes(this.stagingSlot.time_to)
		},
	},

	created() {
		if (!Array.isArray(this.model[this.field.key])) {
			this.$set(this.model, this.field.key, [])
		}
	},

	methods: {
		createStagingSlot() {
			this.stagingSlot = { name: '', time_from: '', time_to: '' }
			this.stagingEditIndex = null
			this.stagingValidationErrors = {}
		},
		editSlot(index) {
			const s = this.slots[index]
			this.stagingSlot = { name: s.name, time_from: s.time_from, time_to: s.time_to }
			this.stagingEditIndex = index
			this.stagingValidationErrors = {}
		},
		cancelStagingSlot() {
			this.stagingSlot = null
			this.stagingEditIndex = null
			this.stagingValidationErrors = {}
		},
		saveStagingSlot() {
			this.validateStagingSlot()
			if (!this.canSaveStaging) return

			const slotData = {
				id: this.isEditingStaging ? this.slots[this.stagingEditIndex].id : Date.now(),
				name: this.stagingSlot.name.trim(),
				time_from: this.stagingSlot.time_from,
				time_to: this.stagingSlot.time_to,
				order: this.isEditingStaging ? this.slots[this.stagingEditIndex].order : this.slots.length,
			}

			const next = this.slots.slice()
			if (this.isEditingStaging) {
				next.splice(this.stagingEditIndex, 1, slotData)
			} else {
				next.push(slotData)
			}
			this.$set(this.model, this.field.key, next)
			this.cancelStagingSlot()
		},
		deleteSlot(index) {
			const next = this.slots.slice()
			next.splice(index, 1)
			// Ricalcola order
			next.forEach((s, i) => s.order = i)
			this.$set(this.model, this.field.key, next)
		},
		validateStagingSlot() {
			const errors = {}
			if (!this.stagingSlot) return errors

			if (!this.stagingSlot.name || !this.stagingSlot.name.trim()) {
				errors.name = 'Descrizione obbligatoria'
			}
			if (!this.isValidTime(this.stagingSlot.time_from)) {
				errors.time_from = 'Formato HH:MM'
			}
			if (!this.isValidTime(this.stagingSlot.time_to)) {
				errors.time_to = 'Formato HH:MM'
			}
			if (this.isValidTime(this.stagingSlot.time_from) && this.isValidTime(this.stagingSlot.time_to)) {
				if (this.timeToMinutes(this.stagingSlot.time_from) >= this.timeToMinutes(this.stagingSlot.time_to)) {
					errors.time_to = 'Deve essere dopo l\'ora di inizio'
				}
			}
			this.stagingValidationErrors = errors
		},
		getStagingValidationError(key) {
			return this.stagingValidationErrors[key] || null
		},
		isValidTime(str) {
			if (!str) return false
			const m = str.match(/^(\d{1,2}):(\d{2})$/)
			if (!m) return false
			const h = parseInt(m[1])
			const min = parseInt(m[2])
			return h >= 0 && h <= 24 && min >= 0 && min <= 59 && !(h === 24 && min > 0)
		},
		timeToMinutes(str) {
			const m = str.match(/^(\d{1,2}):(\d{2})$/)
			if (!m) return 0
			return parseInt(m[1]) * 60 + parseInt(m[2])
		},
	},
}
</script>

<style scoped>
.editing-slot {
	border-color: var(--v-primary-base) !important;
	box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}
</style>
